"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createBulkDeleteOptimisticHandler = createBulkDeleteOptimisticHandler;
exports.createBulkDeleteSuccessHandler = createBulkDeleteSuccessHandler;
exports.createOptimisticBulkDeleteUpdate = createOptimisticBulkDeleteUpdate;
/**
 * 建立批量刪除的樂觀更新處理函數
 */
function createBulkDeleteOptimisticHandler(storeSetState, entityName, getCurrentItems, getCurrentPagination) {
    return (idsToRemove) => {
        const optimisticUpdate = createOptimisticBulkDeleteUpdate(storeSetState, entityName);
        optimisticUpdate(idsToRemove, getCurrentItems(), getCurrentPagination());
    };
}
/**
 * 批量刪除後的通用 onSuccess 處理
 * 自動判斷是否需要跳頁或重新載入當前頁
 */
function createBulkDeleteSuccessHandler(getStoreState, fetchItems, setCurrentPage) {
    return async (deletedIds) => {
        const updatedState = getStoreState();
        const newTotalItems = updatedState.pagination?.totalItems || 0;
        if (newTotalItems > 0) {
            const currentPage = updatedState.currentPage;
            const newTotalPages = updatedState.pagination?.totalPages || 1;
            // 只在當前頁超出範圍時才跳頁，否則留在當前頁
            if (currentPage > newTotalPages) {
                await setCurrentPage(newTotalPages);
            }
            else {
                await fetchItems(currentPage, true); // 強制重新整理，避免顯示快取的舊資料
            }
        }
    };
}
// 批次刪除樂觀更新工具函數
function createOptimisticBulkDeleteUpdate(storeSetState, entityName) {
    return (idsToRemove, currentItems, currentPagination) => {
        // 立即從列表中移除項目
        const newItems = currentItems.filter(item => !idsToRemove.includes(item.id));
        if (!currentPagination) {
            storeSetState({
                items: newItems,
                pagination: null,
                lastOptimisticUpdate: Date.now(),
            });
            return;
        }
        // 計算新的分頁資訊
        const newTotalItems = Math.max(0, currentPagination.totalItems - idsToRemove.length);
        const pageSize = currentPagination.pageSize || 20;
        const newTotalPages = Math.max(1, Math.ceil(newTotalItems / pageSize));
        // 檢查當前頁是否超出範圍，確保 currentPage 不會超出 totalPages
        const currentPage = currentPagination.currentPage;
        const adjustedCurrentPage = Math.min(currentPage, newTotalPages);
        const newPagination = {
            ...currentPagination,
            totalItems: newTotalItems,
            totalPages: newTotalPages,
            currentPage: adjustedCurrentPage
        };
        const timestamp = Date.now();
        // 直接更新 store 狀態
        storeSetState({
            items: newItems,
            pagination: newPagination,
            currentPage: adjustedCurrentPage,
            lastOptimisticUpdate: timestamp,
        });
        // 如果總頁數改變，清除超出範圍的快取，並更新所有保留快取的 pagination
        if (entityName && newTotalPages !== currentPagination.totalPages && typeof localStorage !== 'undefined') {
            try {
                const keys = Object.keys(localStorage);
                const removedKeys = [];
                const updatedKeys = [];
                keys.forEach(key => {
                    if (key.startsWith(`${entityName}-page-`) || key.startsWith(`${entityName}-preload-`)) {
                        const match = key.match(/-(\d+)$/);
                        if (match) {
                            const pageNum = parseInt(match[1]);
                            if (pageNum > newTotalPages) {
                                // 清除超出範圍的快取
                                localStorage.removeItem(key);
                                removedKeys.push(key);
                            }
                            else {
                                // 更新保留快取的 pagination 和 timestamp
                                try {
                                    const cached = localStorage.getItem(key);
                                    if (cached) {
                                        const cacheData = JSON.parse(cached);
                                        if (cacheData.pagination) {
                                            cacheData.pagination = {
                                                ...cacheData.pagination,
                                                totalItems: newTotalItems,
                                                totalPages: newTotalPages,
                                            };
                                            // 更新 timestamp，確保快取被識別為最新的
                                            cacheData.timestamp = timestamp;
                                            localStorage.setItem(key, JSON.stringify(cacheData));
                                            updatedKeys.push(key);
                                        }
                                    }
                                }
                                catch (error) {
                                    // 更新失敗就清除該快取
                                    localStorage.removeItem(key);
                                }
                            }
                        }
                    }
                });
            }
            catch (error) {
                console.warn('更新分頁快取失敗:', error);
            }
        }
    };
}
